import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { useEffect, useRef } from 'react'
import { createDotClusterGroup, dotIcon } from './markerCluster'
import { ESRI_GREY_ATTRIBUTION, ESRI_GREY_REFERENCE_TILES, ESRI_GREY_TILES } from './tiles'

export interface MapDot {
  id: string
  lat: number
  lng: number
  name: string
  distanceKm: number
  /** One line per category: "Category · its sub-categories". Omitted entirely if there is none. */
  subtitles?: string[]
  /** Kept for tooltip/analytics purposes - both tones render the same brand yellow (see below). */
  tone: 'learner' | 'provider'
  /** Undefined until somebody has actually rated them - see ratingRow(). */
  averageRating?: number
  ratingCount?: number
  /** Shows a "Verified" line under the rating. Only ever true for a VERIFIED provider. */
  verified?: boolean
  /** mySATHI holds a 4C Assessment score for them: a violet border and a "4C" tooltip line. */
  fourCScored?: boolean
  /** Violet Dots holds an AI certificate for them: a violet border and an "AI certified" tooltip line. */
  aiCertified?: boolean
  /** Ringed in blue by an operator: a blue border, no tooltip line. Violet wins over it. */
  blueRing?: boolean
  /** What the ring's tooltip line says, in the ring's colour - replaces the "4C" line where set. */
  ringLabel?: string | null
}

/** The border of a 4C-scored or AI-certified dot, selected or not - violet, as in Violet Dots. */
const VIOLET_BORDER = '#7c3aed'
const BLUE_BORDER = '#2563eb'

// Every participant dot is a Yellow Dot, regardless of which side of the match it represents -
// only "You" (the white/navy marker drawn separately below) is a different colour.
const TONE_FILL: Record<MapDot['tone'], string> = {
  learner: '#f59e0b',
  provider: '#f59e0b',
}

/**
 * Leaflet's bindTooltip takes HTML, and these strings carry a participant's own name, so every
 * interpolated value goes through here first. A tutor called `<img src=x onerror=...>` would
 * otherwise be stored XSS that fires the moment someone hovers their pin.
 */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * "4.0 ★★★★☆ (1,175)" under the name.
 *
 * <p>Half a star is rounded to the nearest whole one for the glyphs while the number beside them
 * keeps the real value, so 3.6 reads "3.6 ★★★★☆" - the digits stay honest and the stars stay
 * legible at tooltip size.
 */
function ratingRow(averageRating?: number, ratingCount?: number): string {
  // No rating is not zero stars. An unrated newcomer shown as ☆☆☆☆☆ reads as five people having
  // hated them, which is the opposite of the truth.
  if (averageRating == null || !ratingCount) {
    return '<div style="color:#8a8f9e;font-size:11px;">No ratings yet</div>'
  }
  const filled = Math.round(averageRating)
  const stars = '★'.repeat(filled) + '☆'.repeat(Math.max(0, 5 - filled))
  return (
    '<div style="display:flex;align-items:center;gap:4px;font-size:11px;line-height:1.3;">' +
    `<span style="font-weight:700;color:#1b1f2a;">${averageRating.toFixed(1)}</span>` +
    `<span style="color:#f59e0b;letter-spacing:1px;">${stars}</span>` +
    `<span style="color:#5b6172;">(${ratingCount.toLocaleString()})</span>` +
    '</div>'
  )
}

/**
 * Plain Leaflet rather than a React wrapper: the map owns its own DOM and only needs to be told
 * when the data or the centre changed. Pins are CSS circles (divIcons, so they can be clustered),
 * so no marker image assets have to survive the bundler.
 */
export function ProviderMap({
  centre,
  radiusKm,
  dots,
  selectedId,
  onSelect,
  countLabel,
}: {
  centre: { lat: number; lng: number } | null
  radiusKm: number
  dots: MapDot[]
  selectedId: string | null
  onSelect: (id: string) => void
  countLabel: string
}) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const mapRef = useRef<L.Map | null>(null)
  // "You" and the search radius - never clustered.
  const layerRef = useRef<L.LayerGroup | null>(null)
  // The participant dots - see markerCluster.ts.
  const clusterRef = useRef<L.MarkerClusterGroup | null>(null)
  const onSelectRef = useRef(onSelect)
  onSelectRef.current = onSelect

  // Create once.
  useEffect(() => {
    if (!containerRef.current || mapRef.current) return
    const map = L.map(containerRef.current, { scrollWheelZoom: false }).setView([28.6692, 77.4538], 12)
    const tileOptions = {
      // Esri's grey canvas has no data past z16 in Indian cities - it answers 200 with a flat
      // "Map data not yet available" tile rather than a 404, so nothing here would notice. Capping
      // the native zoom makes Leaflet upscale the z16 tile instead: blurry at z17-19, but the
      // streets stay where they are and the dots stay readable.
      maxNativeZoom: 16,
      maxZoom: 19,
    }
    L.tileLayer(ESRI_GREY_TILES, { attribution: ESRI_GREY_ATTRIBUTION, ...tileOptions }).addTo(map)
    // The street linework and labels, stacked on the base fill above - see tiles.ts.
    L.tileLayer(ESRI_GREY_REFERENCE_TILES, tileOptions).addTo(map)
    layerRef.current = L.layerGroup().addTo(map)
    clusterRef.current = createDotClusterGroup().addTo(map)
    mapRef.current = map
    // On a wide screen the map grows to the height of the panel beside it, which changes as that
    // panel fills in. Leaflet only measures its box when created, so it is told when the box moves
    // - otherwise the new space stays grey with no tiles in it.
    const resized = new ResizeObserver(() => map.invalidateSize())
    resized.observe(containerRef.current)
    return () => {
      resized.disconnect()
      map.remove()
      mapRef.current = null
      layerRef.current = null
      clusterRef.current = null
    }
  }, [])

  // Re-centre when the search origin or radius changes. One tier zoomed in from a plain
  // area-of-the-circle fit, at every radius - street and locality names on this tile style only
  // read clearly from about z14 up (see tiles.ts's Reference layer), so the 5 km default (most
  // common radius) needed its own tier rather than sharing 10 km's.
  useEffect(() => {
    const map = mapRef.current
    if (!map || !centre) return
    const zoom = radiusKm <= 2 ? 15 : radiusKm <= 5 ? 14 : radiusKm <= 10 ? 13 : 11
    map.setView([centre.lat, centre.lng], zoom)
  }, [centre, radiusKm])

  // Redraw the dots.
  useEffect(() => {
    const map = mapRef.current
    const layer = layerRef.current
    const cluster = clusterRef.current
    if (!map || !layer || !cluster) return
    layer.clearLayers()
    cluster.clearLayers()

    if (centre) {
      L.circleMarker([centre.lat, centre.lng], {
        radius: 7,
        color: '#0a192f',
        weight: 3,
        fillColor: '#ffffff',
        fillOpacity: 1,
      })
        .bindTooltip('You are here')
        .addTo(layer)

      L.circle([centre.lat, centre.lng], {
        radius: radiusKm * 1000,
        color: '#f59e0b',
        weight: 1.5,
        dashArray: '6 6',
        fillOpacity: 0.05,
      }).addTo(layer)
    }

    // Built up and handed to the cluster group in one addLayers() call, which is what lets
    // chunkedLoading spread a large batch over several frames.
    const markers: L.Marker[] = []
    for (const dot of dots) {
      const selected = dot.id === selectedId
      const violet = dot.fourCScored || dot.aiCertified
      const ring = violet ? VIOLET_BORDER : dot.blueRing ? BLUE_BORDER : null
      const marker = L.marker([dot.lat, dot.lng], {
        icon: dotIcon({
          radius: selected ? 10 : 7,
          // A 4C-scored or AI-certified dot is ringed in violet (an operator's blue ring otherwise)
          // where any other is navy when selected; unselected, the ring is thicker than a plain
          // dot's so its colour reads at map scale.
          color: ring ?? (selected ? '#0a192f' : TONE_FILL[dot.tone]),
          weight: selected ? 3 : ring ? 2.5 : 1.5,
          fillColor: TONE_FILL[dot.tone],
          fillOpacity: 0.95,
        }),
        // Keeps the selected dot above any neighbour it overlaps once clusters have split.
        zIndexOffset: selected ? 1000 : 0,
        keyboard: false,
      })
        .bindTooltip(
          // Leaflet tooltips never wrap (white-space: nowrap), so a school listing every subject
          // made one line wider than the map and pushed the name off its edge. Wrapped at a fixed
          // width instead: the name always shows in full, and each detail line is kept to two lines.
          '<div style="white-space:normal;width:max-content;max-width:240px;">' +
            `<div style="font-weight:600;color:#1b1f2a;">${escapeHtml(dot.name)} - ${dot.distanceKm} km</div>` +
            (dot.subtitles ?? [])
              .map(
                (line) =>
                  `<div style="color:#5b6172;font-size:11px;line-height:1.35;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden;">${escapeHtml(line)}</div>`,
              )
              .join('') +
            ratingRow(dot.averageRating, dot.ratingCount) +
            (dot.verified
              ? '<div style="color:#047857;font-size:11px;font-weight:600;margin-top:1px;">✓ Verified</div>'
              : '') +
            // An operator's label names the credential behind the ring, in the ring's own colour.
            (ring && dot.ringLabel
              ? `<div style="color:${ring};font-size:11px;font-weight:600;margin-top:1px;">${escapeHtml(dot.ringLabel)}</div>`
              : dot.fourCScored
                ? '<div style="color:#7c3aed;font-size:11px;font-weight:600;margin-top:1px;">4C Assessment taken</div>'
                : '') +
            (dot.aiCertified
              ? '<div style="color:#7c3aed;font-size:11px;font-weight:600;margin-top:1px;">AI certified · Violet Dots</div>'
              : '') +
            '</div>',
          // Centred above the dot rather than beside it, so it does not run off the side of the map.
          { direction: 'top', offset: [0, -6] },
        )
        .on('click', () => onSelectRef.current(dot.id))
      markers.push(marker)
    }
    cluster.addLayers(markers)
  }, [dots, centre, radiusKm, selectedId])

  return (
    // isolate + z-0 pins Leaflet's internal stacking (its panes run to z-index 400-800) inside
    // this box. Without it those panes outrank anything positioned outside the map - dialogs,
    // the sidebar drawer, the sticky header - and paint straight over them.
    //
    // On a wide screen it fills the card it sits in, which the grid stretches to the height of the
    // panel beside it - a fixed height left a band of empty card under the map whenever that panel
    // (the selected person and the nearest list) ran longer. 560px stays the least it will be.
    <div className="relative isolate z-0 lg:h-full">
      <div
        ref={containerRef}
        role="application"
        aria-label="Map of YellowDots participants near you"
        className="h-[340px] w-full rounded-2xl sm:h-[460px] lg:h-full lg:min-h-[560px]"
      />
      <span className="pointer-events-none absolute bottom-3 left-1/2 z-[400] -translate-x-1/2 rounded-full bg-ink-900 px-3 py-1 text-xs font-semibold text-white">
        {countLabel}
      </span>
    </div>
  )
}
